import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import BadPlot from './BadPlot';
import BlurryCover from './BlurryCover';
import BossFight from './BossFight';
import GamePoster from './Posters';
import { moveFocus } from './Quiz';
import { GAMES, loadBest } from './game';

// The arcade screen is laid out at a fixed resolution, like a real arcade board,
// then mapped onto the cabinet's 3D screen (see Room.setArcadeScreen).
export const SCREEN_W = 480;
export const SCREEN_H = 640;

// title → select (poster carousel) → info (how to play) → play
type View = 'title' | 'select' | 'info' | 'play';

const hi = (n: number) => String(n).padStart(3, '0');

// Runs on the cabinet's screen. `attach` hands the screen element to the 3D room, which keeps it
// glued to the cabinet every frame; without a room (no WebGL) the screen floats centered instead.
export default function Arcade({ onClose, attach }: { onClose: () => void; attach: (el: HTMLElement | null) => boolean }) {
  const [view, setView] = useState<View>('title');
  const [idx, setIdx] = useState(0);
  const [floating, setFloating] = useState(false);
  const [scale, setScale] = useState(1);
  const root = useRef<HTMLDivElement>(null);
  const screen = useRef<HTMLDivElement>(null);
  const game = GAMES[idx];

  useLayoutEffect(() => {
    setFloating(!attach(screen.current));
    return () => { attach(null); };
  }, [attach]);

  useEffect(() => {
    if (!floating) return;
    const fit = () => setScale(Math.min((innerWidth - 32) / SCREEN_W, (innerHeight - 120) / SCREEN_H));
    fit();
    addEventListener('resize', fit);
    return () => removeEventListener('resize', fit);
  }, [floating]);

  // Return focus to whatever opened the arcade, and lock page scroll so the camera stays put.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const html = document.documentElement, prev = html.style.overflow;
    html.style.overflow = 'hidden';
    return () => { html.style.overflow = prev; opener?.focus?.({ preventScroll: true }); };
  }, []);

  // Each screen focuses its main button, so Enter always does the obvious thing.
  useEffect(() => {
    const sel = { title: '.arc-press', select: '.arc-poster', info: '.arc-go', play: null }[view];
    if (sel) screen.current?.querySelector<HTMLElement>(sel)?.focus({ preventScroll: true });
  }, [view, idx]);

  const cycle = (dir: number) => setIdx(i => (i + dir + GAMES.length) % GAMES.length);

  // Cabinet controls live on the window, so they keep working even if focus slips out of the screen.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      const inside = !!root.current?.contains(document.activeElement);
      if (e.key === 'Escape') {
        e.preventDefault();
        if (view === 'title') onClose(); else setView(view === 'select' ? 'title' : 'select');
      } else if (view === 'select' && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        // Left/right flip through the game posters.
        e.preventDefault();
        cycle(e.key === 'ArrowLeft' ? -1 : 1);
      } else if (!inside && e.key.startsWith('Arrow')) {
        // A stray press with focus outside the arcade lands back on the screen.
        e.preventDefault();
        screen.current?.querySelector<HTMLElement>('button')?.focus({ preventScroll: true });
      } else if (e.key === 'Tab' && root.current) {
        // Keep Tab inside the arcade.
        const f = [...root.current.querySelectorAll<HTMLElement>('button:not([disabled])')];
        const first = f[0], last = f[f.length - 1];
        if (!inside || (e.shiftKey && document.activeElement === first)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [view, onClose]);

  return (
    <div ref={root} className="arcade" role="dialog" aria-modal="true" aria-label="Isak's arcade">
      <div ref={screen} data-theme={view === 'title' ? undefined : game.id} className={`arc-screen${floating ? ' is-floating' : ''}`}
        style={{ width: SCREEN_W, height: SCREEN_H, ...(floating && { transform: `translate(-50%, -50%) scale(${scale})` }) }}>
        {view === 'title' && (
          <div className="arc-title-screen">
            <p className="arc-logo">Isak's<br />arcade</p>
            <button type="button" className="arc-press" onClick={() => setView('select')}>Press start</button>
            <div className="arc-scores">
              <p>Hi-scores</p>
              {GAMES.map(g => <p key={g.id}><span>{g.title}</span><span>{hi(loadBest(g.id))}</span></p>)}
            </div>
            <p className="arc-credit">© Isak Gerre · 1 credit</p>
          </div>
        )}

        {view === 'select' && (
          <div className="arc-select">
            <p className="arc-heading">Select game</p>
            <div className="arc-carousel">
              <button type="button" className="arc-arrow" onClick={() => cycle(-1)} aria-label="Previous game">◀</button>
              <button type="button" className="arc-poster" onClick={() => setView('info')}
                aria-label={`${game.title}: ${game.tagline}. Hi-score ${loadBest(game.id)}. Press Enter to choose.`}>
                <GamePoster id={game.id} />
              </button>
              <button type="button" className="arc-arrow" onClick={() => cycle(1)} aria-label="Next game">▶</button>
            </div>
            <div className="arc-dots" aria-hidden="true">
              {GAMES.map((g, i) => <span key={g.id} className={i === idx ? 'is-on' : ''} />)}
            </div>
            <p className="arc-hint">◀ ▶ browse · Enter select · Hi {hi(loadBest(game.id))}</p>
          </div>
        )}

        {view === 'info' && (
          <div className="arc-info">
            <p className="arc-info-kicker">{game.group} · {game.tagline}</p>
            <h3 className="arc-info-title">{game.title}</h3>
            <p className="arc-info-label">How to play</p>
            <ol className="arc-rules">{game.rules.map(r => <li key={r}>{r}</li>)}</ol>
            <p className="arc-love"><span aria-hidden="true">♥</span> {game.love}</p>
            <div className="arc-info-actions" onKeyDown={moveFocus}>
              <button type="button" className="arc-btn arc-go" onClick={() => setView('play')}>Start ▶</button>
              <button type="button" className="arc-btn arc-btn-ghost" onClick={() => setView('select')}>◀ Back</button>
            </div>
          </div>
        )}

        {view === 'play' && game.id === 'blur' && <BlurryCover onExit={() => setView('select')} />}
        {view === 'play' && game.id === 'plot' && <BadPlot onExit={() => setView('select')} />}
        {view === 'play' && game.id === 'boss' && <BossFight onExit={() => setView('select')} />}
      </div>
      <div className="arc-controls">
        {view === 'play' && <button type="button" className="arc-leave" onClick={() => setView('select')}>◀ Games</button>}
        <button type="button" className="arc-leave" onClick={onClose}>Leave arcade ✕</button>
      </div>
    </div>
  );
}
